import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropContext, Draggable, DropResult } from '@hello-pangea/dnd';
import { GripVertical, FileText, Loader2, Save, RotateCcw, AlertCircle, Search, MapPin } from 'lucide-react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useToast } from '../../context/ToastContext';
import { StrictModeDroppable } from '../common/StrictModeDroppable';

import { logger, getErrorMessage } from '../../utils/logger';
import { API_BASE_URL } from '../../config/api';  // ✅ Industry Standard: Environment-aware URLs
interface TestReorderListProps {
  isOpen: boolean;
  onClose: () => void;
  tag: string;
  initialOrder: string[];
  onSave: (order: string[]) => void;
}

interface TestFile {
  id: string;
  name: string;
  flowId: string;
}

const TestReorderList: React.FC<TestReorderListProps> = ({
  isOpen,
  onClose,
  tag,
  initialOrder,
  onSave
}) => {
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState<TestFile[]>([]);
  const [orderedTests, setOrderedTests] = useState<TestFile[]>([]);
  
  // Infinite Scroll State
  const [displayedTests, setDisplayedTests] = useState<TestFile[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const BATCH_SIZE = 25;
  const sentinelRef = useRef<HTMLDivElement>(null);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTests, setFilteredTests] = useState<TestFile[]>([]);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  
  // Auto-scroll State
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch tests by tag
  useEffect(() => {
    if (!isOpen || !tag) return;

    const fetchTests = async () => {
      setLoading(true);
      try {
        // Fetch flows with the tag
        const response = await fetch(`${API_BASE_URL}/api/tags/${encodeURIComponent(tag)}/flows`);
        if (!response.ok) throw new Error('Failed to fetch tests');

        const data = await response.json();
        const flows = data.flows || [];

        // Map to test file objects
        const testFiles: TestFile[] = flows.map((flow: any) => ({
          id: flow.id,
          name: flow.name,
          flowId: flow.id
        }));

        setTests(testFiles);

        // Apply initial order if provided
        if (initialOrder.length > 0) {
          const ordered = applyInitialOrder(testFiles, initialOrder);
          setOrderedTests(ordered);
        } else {
          // Default alphabetical order
          const sorted = [...testFiles].sort((a, b) => a.name.localeCompare(b.name));
          setOrderedTests(sorted);
        }
      } catch (error: any) {
        logger.error('Failed to fetch tests:', { error: getErrorMessage(error) });
        showError(`Failed to load tests: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchTests();
  }, [isOpen, tag, initialOrder, showError]);

  // Apply initial order
  const applyInitialOrder = (testFiles: TestFile[], order: string[]): TestFile[] => {
    const orderMap = new Map(order.map((id, index) => [id, index]));
    
    return [...testFiles].sort((a, b) => {
      const aIndex = orderMap.get(a.flowId) ?? orderMap.get(a.id) ?? 999;
      const bIndex = orderMap.get(b.flowId) ?? orderMap.get(b.id) ?? 999;
      return aIndex - bIndex;
    });
  };

  // Initialize filtered tests when orderedTests change
  useEffect(() => {
    if (orderedTests.length > 0) {
      setFilteredTests(orderedTests);
      setDisplayedTests(orderedTests.slice(0, BATCH_SIZE));
    }
  }, [orderedTests, BATCH_SIZE]);

  // Debounced Search Effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!searchQuery.trim()) {
        setFilteredTests(orderedTests);
        setDisplayedTests(orderedTests.slice(0, BATCH_SIZE));
      } else {
        const filtered = orderedTests.filter(test =>
          test.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredTests(filtered);
        setDisplayedTests(filtered.slice(0, BATCH_SIZE));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, orderedTests, BATCH_SIZE]);

  // Load more tests (infinite scroll)
  const loadMoreTests = useCallback(() => {
    setLoadingMore(true);
    setTimeout(() => {
      const nextBatch = filteredTests.slice(
        displayedTests.length,
        displayedTests.length + BATCH_SIZE
      );
      setDisplayedTests(prev => [...prev, ...nextBatch]);
      setLoadingMore(false);
    }, 300);
  }, [filteredTests, displayedTests.length, BATCH_SIZE]);

  // Infinite Scroll Observer
  useEffect(() => {
    if (!sentinelRef.current || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayedTests.length < filteredTests.length) {
          loadMoreTests();
        }
      },
      { threshold: 0.5, rootMargin: '100px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [displayedTests, filteredTests, loadingMore, loadMoreTests]);

  // Auto-scroll Effect
  useEffect(() => {
    if (!isDragging || !containerRef.current) return;

    let animationFrameId: number;
    const EDGE_SIZE = 50;
    const SCROLL_SPEED = 5;

    const handleAutoScroll = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const mouseY = e.clientY;

      // Near top edge - scroll up
      if (mouseY < rect.top + EDGE_SIZE && mouseY > rect.top) {
        animationFrameId = requestAnimationFrame(() => {
          container.scrollTop -= SCROLL_SPEED;
        });
      }
      // Near bottom edge - scroll down
      else if (mouseY > rect.bottom - EDGE_SIZE && mouseY < rect.bottom) {
        animationFrameId = requestAnimationFrame(() => {
          container.scrollTop += SCROLL_SPEED;
        });
      }
    };

    document.addEventListener('mousemove', handleAutoScroll);
    return () => {
      document.removeEventListener('mousemove', handleAutoScroll);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isDragging]);

  // Jump to position function
  const handleJumpToPosition = (testId: string) => {
    // Clear search to show full list
    setSearchQuery('');
    setFilteredTests(orderedTests);
    
    // Find test index
    const testIndex = orderedTests.findIndex(t => t.id === testId);
    if (testIndex === -1) return;
    
    // Load enough batches to reach this test
    const batchesNeeded = Math.ceil((testIndex + 1) / BATCH_SIZE);
    const itemsToLoad = batchesNeeded * BATCH_SIZE;
    setDisplayedTests(orderedTests.slice(0, itemsToLoad));
    
    // Scroll to test after render
    setTimeout(() => {
      const testElement = document.querySelector(`[data-test-id="${testId}"]`);
      if (testElement) {
        testElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Highlight for 3 seconds
        setHighlightedId(testId);
        setTimeout(() => setHighlightedId(null), 3000);
      }
    }, 100);
  };

  // Handle drag end
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedTests);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedTests(items);
  };

  // Reset to alphabetical
  const handleReset = () => {
    const sorted = [...tests].sort((a, b) => a.name.localeCompare(b.name));
    setOrderedTests(sorted);
  };

  // Save order
  const handleSave = () => {
    const order = orderedTests.map(test => test.flowId || test.id);
    onSave(order);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Customize Test Execution Order"
      size="lg"
    >
      <div className="space-y-4">
        {/* Info */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 dark:text-blue-300 mb-1">
                Drag and drop tests to reorder them for sequential execution.
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-400">
                Tag: <strong>{tag}</strong> • {orderedTests.length} test(s)
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        {!loading && orderedTests.length > 0 && (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
              <input
                type="text"
                placeholder="Search tests by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isDragging}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            
            {searchQuery && (
              <div className="text-sm text-gray-600 dark:text-slate-400">
                Found {filteredTests.length} test(s) matching "{searchQuery}"
              </div>
            )}
          </div>
        )}

        {/* Search Results Panel - Jump to Position */}
        {searchQuery && filteredTests.length > 0 && !loading && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-3">
              Search Results - Click "Jump to Position" to locate and reorder
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {filteredTests.map((test) => {
                const actualPosition = orderedTests.findIndex(t => t.id === test.id) + 1;
                return (
                  <div key={test.id} className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded border border-gray-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileText className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                        {test.name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-slate-500 flex-shrink-0">
                        Position #{actualPosition}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleJumpToPosition(test.id)}
                      icon={<MapPin className="w-4 h-4" />}
                      className="flex-shrink-0 ml-2"
                    >
                      Jump
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Test List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : orderedTests.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-slate-400">
              No tests found with tag {tag}
            </p>
          </div>
        ) : (
          <div 
            ref={containerRef}
            className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-y-auto max-h-[500px]"
          >
            <DragDropContext 
              onDragStart={() => setIsDragging(true)}
              onDragEnd={(result) => {
                setIsDragging(false);
                handleDragEnd(result);
              }}
            >
              <StrictModeDroppable droppableId="tests">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`${
                      snapshot.isDraggingOver
                        ? 'bg-indigo-50 dark:bg-indigo-900/20'
                        : 'bg-white dark:bg-slate-800'
                    } transition-colors`}
                  >
                    {displayedTests.map((test, index) => (
                      <Draggable
                        key={test.id}
                        draggableId={test.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            data-test-id={test.id}
                            className={`flex items-center gap-3 p-4 border-b border-gray-200 dark:border-slate-700 last:border-b-0 ${
                              snapshot.isDragging
                                ? 'bg-indigo-100 dark:bg-indigo-900/30 shadow-lg'
                                : 'bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-750'
                            } ${
                              highlightedId === test.id
                                ? 'animate-pulse ring-2 ring-indigo-500'
                                : ''
                            } transition-colors`}
                          >
                            {/* Drag Handle */}
                            <div 
                              {...provided.dragHandleProps}
                              className="cursor-grab active:cursor-grabbing flex-shrink-0"
                            >
                              <GripVertical className="w-5 h-5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors" />
                            </div>

                            {/* Order Number */}
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold text-sm flex-shrink-0">
                              {index + 1}
                            </div>

                            {/* Test Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                <span className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                                  {test.name}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-slate-500 truncate mt-0.5">
                                Flow ID: {test.flowId || test.id}
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    
                    {/* Sentinel for infinite scroll */}
                    {displayedTests.length < filteredTests.length && (
                      <div ref={sentinelRef} className="h-4" />
                    )}
                    
                    {/* Loading indicator */}
                    {loadingMore && (
                      <div className="flex items-center justify-center py-4 bg-white dark:bg-slate-800">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">
                          Loading more tests...
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </StrictModeDroppable>
            </DragDropContext>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-slate-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleReset}
            disabled={loading || orderedTests.length === 0}
            icon={<RotateCcw className="w-4 h-4" />}
          >
            Reset to Alphabetical
          </Button>

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={loading || orderedTests.length === 0}
              icon={<Save className="w-4 h-4" />}
              className="!bg-indigo-600 dark:!bg-indigo-700 hover:!bg-indigo-700 dark:hover:!bg-indigo-600"
            >
              Save Order
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TestReorderList;

